import React from "react";

function Pill({ active, disabled, children, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        "rounded-full px-4 py-2 text-sm font-semibold transition border",
        disabled
          ? "opacity-40 cursor-not-allowed"
          : active
          ? "hover:bg-blue-700"
          : "hover:bg-gray-50",
        active
          ? "bg-blue-600 text-white border-blue-700 shadow"
          : "bg-white text-gray-800 border-gray-200",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export default function SequentialFilters({ value, onChange, className = "" }) {
  const categories = [
    { id: "all", label: "Όλα" },
    { id: "voucher", label: "Voucher" },
    { id: "selfFunded", label: "Αυτοχρηματοδοτούμενα" },
  ];

  const audiences = [
    { id: "unemployed", label: "Άνεργοι" },
    { id: "employed", label: "Εργαζόμενοι" },
  ];

  const statuses = [
    { id: "all", label: "Όλα" },
    { id: "open", label: "Ανοιχτά" },
    { id: "upcoming", label: "Επερχόμενα" },
    { id: "closed", label: "Ολοκληρωμένα" },
  ];

  // ✅ When category = all, other filters are irrelevant/ignored.
  const isAllCategory = value.typeFilter === "all";

  const needsAudience = value.typeFilter === "voucher";
  const hasAudience = !!value.voucherAudienceFilter;

  // Status can be picked only after voucher audience is chosen (voucher flow),
  // BUT when category=all, we disable status entirely because it's ignored.
  const canPickStatus = !isAllCategory && (needsAudience ? hasAudience : true);

  function setCategory(id) {
    if (id === "all") {
      // Override mode: ignore everything else
      onChange({
        typeFilter: "all",
        voucherAudienceFilter: null,
        statusFilter: "all",
      });
      return;
    }

    onChange({
      typeFilter: id,
      // if voucher, ensure audience exists for the flow
      voucherAudienceFilter: id === "voucher" ? (value.voucherAudienceFilter ?? "unemployed") : null,
      statusFilter: "all",
    });
  }

  function setAudience(id) {
    onChange({
      ...value,
      voucherAudienceFilter: id,
      statusFilter: value.statusFilter ?? "all",
    });
  }

  function setStatus(id) {
    onChange({
      ...value,
      statusFilter: id,
    });
  }

  return (
    <div className={["rounded-2xl border border-gray-200 bg-gray-50 p-4 sm:p-5", className].join(" ")}>
      {/* 1) Category */}
      <div className="space-y-2">
        <div className="text-sm font-extrabold text-gray-900">Κατηγορία</div>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <Pill key={c.id} active={value.typeFilter === c.id} onClick={() => setCategory(c.id)}>
              {c.label}
            </Pill>
          ))}
        </div>

        {isAllCategory && (
          <div className="text-xs text-gray-500">
            Στο <span className="font-semibold">Όλα</span> εμφανίζονται όλα τα ενεργά προγράμματα (ανεξαρτήτως κατηγορίας/δικαιούχων/κατάστασης).
          </div>
        )}
      </div>

      {/* 2) Audience (only if voucher, and NOT in all-mode) */}
      {needsAudience && !isAllCategory && (
        <div className="mt-4 space-y-2">
          <div className="text-sm font-extrabold text-gray-900">Δικαιούχοι</div>
          <div className="flex flex-wrap gap-2">
            {audiences.map((a) => (
              <Pill
                key={a.id}
                active={value.voucherAudienceFilter === a.id}
                onClick={() => setAudience(a.id)}
              >
                {a.label}
              </Pill>
            ))}
          </div>
        </div>
      )}

      {/* 3) Status */}
      <div className="mt-4 space-y-2">
        <div className="text-sm font-extrabold text-gray-900">Κατάσταση</div>
        <div className="flex flex-wrap gap-2">
          {statuses.map((s) => (
            <Pill
              key={s.id}
              disabled={!canPickStatus}
              active={(value.statusFilter ?? "all") === s.id}
              onClick={() => setStatus(s.id)}
            >
              {s.label}
            </Pill>
          ))}
        </div>

        {isAllCategory ? (
          <div className="text-xs text-gray-500">
            Η κατάσταση αγνοείται στο <span className="font-semibold">Όλα</span>.
          </div>
        ) : !canPickStatus ? (
          <div className="text-xs text-gray-500">Διάλεξε πρώτα Δικαιούχους.</div>
        ) : null}
      </div>

      {/* Clear */}
      <div className="mt-4">
        <button
          type="button"
          className="text-xs font-semibold text-gray-700 hover:text-gray-900 underline underline-offset-2"
          onClick={() =>
            onChange({
              typeFilter: "all",
              voucherAudienceFilter: null,
              statusFilter: "all",
            })
          }
        >
          Καθαρισμός
        </button>
      </div>
    </div>
  );
}